import { useState } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Spin,
  Empty,
  Space,
  Drawer,
  Descriptions,
  Collapse,
  Input,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  useGetScheduleRunsQuery,
  useGetScheduleRunDetailQuery,
  useCompareScheduleRunsQuery,
  useLazyValidateScheduleQuery,
} from "../../../redux/api/scheduling";
import { formatDateTime, formatShortDateTime } from "../_shared/utils/dateUtils";

const ENGINE_COLORS: Record<string, string> = {
  heuristic: "blue",
  cp_sat: "purple",
  headroom: "green",
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  completed: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
  failed: <CloseCircleOutlined style={{ color: "#cf1322" }} />,
  running: <SyncOutlined spin style={{ color: "#faad14" }} />,
};

export default function ScheduleRunsPanel() {
  const { data: runs, isLoading } = useGetScheduleRunsQuery(undefined);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<[number, number] | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [jobFilter, setJobFilter] = useState<string>("");
  const [triggerValidate, { data: validationData, isFetching: isValidating }] = useLazyValidateScheduleQuery();

  const columns = [
    {
      title: "Engine",
      dataIndex: "engine_type",
      key: "engine_type",
      render: (v: string) => (
        <Tag color={ENGINE_COLORS[v] || "default"}>
          {v === "cp_sat" ? "CP-SAT" : v === "headroom" ? "Headroom" : "Heuristic"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => (
        <Space>
          {STATUS_ICONS[v]}
          {v.charAt(0).toUpperCase() + v.slice(1)}
        </Space>
      ),
    },
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      render: (v: string) => (v ? formatDateTime(v, "MMM D, YYYY h:mm A") : "-"),
    },
    {
      title: "Duration",
      dataIndex: "duration_seconds",
      key: "duration_seconds",
      render: (v: number) => (v != null ? `${v.toFixed(1)}s` : "-"),
    },
    {
      title: "Jobs",
      dataIndex: "total_jobs",
      key: "total_jobs",
    },
    {
      title: "On Track",
      dataIndex: "on_track_count",
      key: "on_track_count",
      render: (v: number) => <Tag color="green">{v}</Tag>,
    },
    {
      title: "At Risk",
      dataIndex: "at_risk_count",
      key: "at_risk_count",
      render: (v: number) => (
        <Tag color={v > 0 ? "orange" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Late",
      dataIndex: "late_count",
      key: "late_count",
      render: (v: number) => (
        <Tag color={v > 0 ? "red" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, record: any) => (
        <Button size="small" onClick={() => setSelectedRunId(record.id)}>
          Detail
        </Button>
      ),
    },
  ];

  if (isLoading) return <Spin size="large" className="mt-20 mx-auto block" />;

  return (
    <>
      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <Button
          icon={<SwapOutlined />}
          disabled={selectedRows.length !== 2}
          onClick={() => setCompareIds([selectedRows[0], selectedRows[1]])}
        >
          Compare Selected
        </Button>
        {compareIds && (
          <Button onClick={() => setCompareIds(null)}>Clear Comparison</Button>
        )}
      </div>

      <Table
        dataSource={runs || []}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedRows,
          onChange: (keys) => setSelectedRows(keys as number[]),
        }}
      />

      <Card
        title="Schedule Validation"
        style={{ marginTop: 24 }}
        extra={
          <Space>
            <Input
              placeholder="Filter by order name"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Button
              type="primary"
              onClick={() => triggerValidate(jobFilter ? { job: jobFilter } : undefined)}
              loading={isValidating}
            >
              Run Validation
            </Button>
          </Space>
        }
      >
        {validationData && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Space size="large">
                <Tag color="green">{validationData.total_pass} passed</Tag>
                <Tag color="red">{validationData.total_fail} failed</Tag>
                {validationData.total_skip > 0 && (
                  <Tag color="orange">{validationData.total_skip} skipped</Tag>
                )}
                <span>
                  {validationData.task_count} tasks across {validationData.job_count} jobs
                </span>
              </Space>
            </div>
            <Table
              dataSource={validationData.checks}
              rowKey="key"
              size="small"
              pagination={false}
              expandable={{
                expandedRowRender: (record: any) =>
                  record.violations?.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 20, fontFamily: "monospace", fontSize: 12 }}>
                      {record.violations.map((v: string, i: number) => (
                        <li key={i}>{v}</li>
                      ))}
                      {record.violation_count > record.violations.length && (
                        <li style={{ color: "#999" }}>
                          ... and {record.violation_count - record.violations.length} more
                        </li>
                      )}
                    </ul>
                  ) : null,
                rowExpandable: (record: any) => record.violations?.length > 0,
              }}
              columns={[
                {
                  title: "Check",
                  dataIndex: "key",
                  key: "key",
                  width: 70,
                  sorter: (a: any, b: any) => a.key.localeCompare(b.key),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 80,
                  filters: [
                    { text: "PASS", value: "PASS" },
                    { text: "FAIL", value: "FAIL" },
                    { text: "SKIP", value: "SKIP" },
                  ],
                  onFilter: (value: any, record: any) => record.status === value,
                  render: (status: string) => {
                    const color = status === "PASS" ? "green" : status === "FAIL" ? "red" : "orange";
                    return <Tag color={color}>{status}</Tag>;
                  },
                },
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                },
                {
                  title: "Violations",
                  dataIndex: "violation_count",
                  key: "violation_count",
                  width: 90,
                  render: (count: number, record: any) =>
                    record.status === "FAIL" ? (
                      <Tag color="red">{count}</Tag>
                    ) : null,
                },
              ]}
            />
          </>
        )}
      </Card>

      <RunDetailDrawer
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />

      {compareIds && (
        <CompareView runA={compareIds[0]} runB={compareIds[1]} />
      )}
    </>
  );
}

function RunDetailDrawer({
  runId,
  onClose,
}: {
  runId: number | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetScheduleRunDetailQuery(runId!, {
    skip: !runId,
  });

  return (
    <Drawer
      title={data ? `Run #${data.id} - ${data.engine_type === "cp_sat" ? "CP-SAT" : "Heuristic"}` : "Run Detail"}
      open={!!runId}
      onClose={onClose}
      width={720}
    >
      {isLoading ? (
        <Spin />
      ) : data ? (
        <>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Engine">
              <Tag color={ENGINE_COLORS[data.engine_type]}>{data.engine_type === "cp_sat" ? "CP-SAT" : "Heuristic"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">{data.status}</Descriptions.Item>
            <Descriptions.Item label="Started">{formatDateTime(data.started_at, "MMM D, YYYY h:mm:ss A")}</Descriptions.Item>
            <Descriptions.Item label="Duration">{data.duration_seconds?.toFixed(1)}s</Descriptions.Item>
            <Descriptions.Item label="Jobs">{data.total_jobs}</Descriptions.Item>
            <Descriptions.Item label="Tasks">{data.total_tasks}</Descriptions.Item>
            <Descriptions.Item label="On Track"><Tag color="green">{data.on_track_count}</Tag></Descriptions.Item>
            <Descriptions.Item label="At Risk"><Tag color="orange">{data.at_risk_count}</Tag></Descriptions.Item>
            <Descriptions.Item label="Late"><Tag color="red">{data.late_count}</Tag></Descriptions.Item>
          </Descriptions>

          {data.engine_type === "headroom" && data.solver_stats && (
            <Card size="small" title="Headroom Details" style={{ marginTop: 16 }}>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Scheduler Passes">{data.solver_stats.scheduler_passes ?? "-"}</Descriptions.Item>
                <Descriptions.Item label="Phantom Jobs Tested">{data.solver_stats.phantom_jobs_tested ?? "-"}</Descriptions.Item>
                <Descriptions.Item label="Templates">{data.solver_stats.templates ?? "-"}</Descriptions.Item>
              </Descriptions>
              {data.solver_stats.matrix && Object.keys(data.solver_stats.matrix).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong>Capacity Matrix:</strong>
                  <pre style={{ fontSize: 12, marginTop: 4 }}>
                    {JSON.stringify(data.solver_stats.matrix, null, 2)}
                  </pre>
                </div>
              )}
            </Card>
          )}

          {data.engine_type !== "headroom" && data.solver_stats && Object.keys(data.solver_stats).length > 0 && (
            <Collapse
              style={{ marginTop: 16 }}
              items={[
                {
                  key: "stats",
                  label: "Solver Statistics",
                  children: (
                    <pre style={{ fontSize: 12 }}>
                      {JSON.stringify(data.solver_stats, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          )}

          {data.error_message && (
            <Card
              size="small"
              title="Error"
              style={{ marginTop: 16, borderColor: "#cf1322" }}
            >
              <pre style={{ fontSize: 12, color: "#cf1322" }}>
                {data.error_message}
              </pre>
            </Card>
          )}

          <Card size="small" title="Task Snapshots" style={{ marginTop: 16 }}>
            <Table
              dataSource={data.run_tasks || []}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, size: "small" }}
              columns={[
                { title: "Job", dataIndex: "job_order_number", key: "job", width: 160 },
                { title: "Task", dataIndex: "task_name", key: "task", width: 140 },
                {
                  title: "Start",
                  dataIndex: "start_date",
                  key: "start",
                  render: (v: string) => v ? formatShortDateTime(v) : "-",
                },
                {
                  title: "End",
                  dataIndex: "end_date",
                  key: "end",
                  render: (v: string) => v ? formatShortDateTime(v) : "-",
                },
                { title: "Machine", dataIndex: "assigned_machine_name", key: "machine" },
                { title: "Staff", dataIndex: "assigned_staff_name", key: "staff" },
                {
                  title: "Timeliness",
                  dataIndex: "job_timeliness",
                  key: "timeliness",
                  render: (v: string) => {
                    const color = v === "late" ? "red" : v === "at_risk" ? "orange" : v === "on_track" ? "green" : "default";
                    return v ? <Tag color={color}>{v.replace("_", " ")}</Tag> : "-";
                  },
                },
              ]}
            />
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </Drawer>
  );
}

function CompareView({ runA, runB }: { runA: number; runB: number }) {
  const { data, isLoading } = useCompareScheduleRunsQuery(
    { runA, runB },
    { skip: !runA || !runB }
  );

  if (isLoading) return <Spin className="mt-4" />;
  if (!data) return null;

  const changedCount = data.tasks?.filter((t: any) => t.changed).length || 0;

  return (
    <Card
      title={`Comparison: Run #${data.run_a?.id} vs Run #${data.run_b?.id}`}
      style={{ marginTop: 16 }}
      extra={
        <Tag color={changedCount > 0 ? "orange" : "green"}>
          {changedCount} difference{changedCount !== 1 ? "s" : ""}
        </Tag>
      }
    >
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <Descriptions size="small" bordered column={1} title={`Run A (#${data.run_a?.id})`} style={{ flex: 1 }}>
          <Descriptions.Item label="Engine">
            <Tag color={ENGINE_COLORS[data.run_a?.engine_type]}>{data.run_a?.engine_type === "cp_sat" ? "CP-SAT" : "Heuristic"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="On Track / At Risk / Late">
            <Space>
              <Tag color="green">{data.run_a?.on_track_count}</Tag>
              <Tag color="orange">{data.run_a?.at_risk_count}</Tag>
              <Tag color="red">{data.run_a?.late_count}</Tag>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Duration">{data.run_a?.duration_seconds?.toFixed(1)}s</Descriptions.Item>
        </Descriptions>

        <Descriptions size="small" bordered column={1} title={`Run B (#${data.run_b?.id})`} style={{ flex: 1 }}>
          <Descriptions.Item label="Engine">
            <Tag color={ENGINE_COLORS[data.run_b?.engine_type]}>{data.run_b?.engine_type === "cp_sat" ? "CP-SAT" : "Heuristic"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="On Track / At Risk / Late">
            <Space>
              <Tag color="green">{data.run_b?.on_track_count}</Tag>
              <Tag color="orange">{data.run_b?.at_risk_count}</Tag>
              <Tag color="red">{data.run_b?.late_count}</Tag>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Duration">{data.run_b?.duration_seconds?.toFixed(1)}s</Descriptions.Item>
        </Descriptions>
      </div>

      <Table
        dataSource={data.tasks || []}
        rowKey="key"
        size="small"
        pagination={{ pageSize: 25, size: "small" }}
        rowClassName={(record: any) => (record.changed ? "bg-yellow-50" : "")}
        columns={[
          { title: "Job", dataIndex: "job_order_number", key: "job", width: 160 },
          { title: "Task", dataIndex: "task_name", key: "task", width: 140 },
          {
            title: "Run A Start",
            key: "a_start",
            render: (_: unknown, r: any) =>
              r.run_a?.start_date ? formatShortDateTime(r.run_a.start_date) : "-",
          },
          {
            title: "Run B Start",
            key: "b_start",
            render: (_: unknown, r: any) =>
              r.run_b?.start_date ? formatShortDateTime(r.run_b.start_date) : "-",
          },
          {
            title: "Run A End",
            key: "a_end",
            render: (_: unknown, r: any) =>
              r.run_a?.end_date ? formatShortDateTime(r.run_a.end_date) : "-",
          },
          {
            title: "Run B End",
            key: "b_end",
            render: (_: unknown, r: any) =>
              r.run_b?.end_date ? formatShortDateTime(r.run_b.end_date) : "-",
          },
          {
            title: "Changed",
            dataIndex: "changed",
            key: "changed",
            width: 80,
            render: (v: boolean) =>
              v ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>,
            filters: [
              { text: "Changed", value: true },
              { text: "Same", value: false },
            ],
            onFilter: (value: any, record: any) => record.changed === value,
          },
        ]}
      />
    </Card>
  );
}
